import React from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Divider
} from '@mui/material';
import {
    EmojiEvents,
    CatchingPokemon,
    Map,
    Visibility,
    PhoneAndroid,
    Apple,
    MoreVert,
    Share,
    FlashOn,
    ViewCompact,
    Launch,
} from '@mui/icons-material';
import IosShareIcon from '@mui/icons-material/IosShare';

const Start = () => {
    return (
        <Box
            sx={{
                padding: 3,
                maxWidth: '800px',
                mx: 'auto',
                paddingBottom: { xs: "96px", sm: "64px", md: "120px" }
            }}
        >
            <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                Willkommen in deiner Pokémon GO Verwaltung
            </Typography>

            <Typography variant="body1" sx={{ marginBottom: 4, textAlign: 'center' }}>
                Mit dieser App kannst du deine Fortschritte im Spiel dokumentieren und mit anderen teilen.
            </Typography>

            <Paper elevation={3} sx={{ padding: 3, borderRadius: 2, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
                    Was kannst du in der App machen?
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <EmojiEvents color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Medaillen in deinem Profil verwalten" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CatchingPokemon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Gefangene Pokémon eintragen" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CatchingPokemon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Fehlende Pokémon eintragen" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <Map color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Arenen erfassen, denen du begegnet bist" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <Visibility color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Dein Profil für andere Spieler sichtbar machen, damit noch schneller getauscht werden kann" />
                    </ListItem>
                </List>
            </Paper>

            <Divider sx={{ my: 4 }} />

            <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                    So fügst du diese Seite als "App" zu deinem Homescreen hinzu
                </Typography>

                <Typography variant="body2" sx={{ mb: 2 }}>
                    Um deine Pokémon GO Verwaltung noch schneller und bequemer zu nutzen, kannst du sie wie eine App direkt auf den Startbildschirm deines Smartphones oder Tablets legen. So hast du direkten Zugriff, ohne jedes Mal den Browser öffnen zu müssen!
                </Typography>

                {/* Vorteile hinzugefügt */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        Vorteile der Homescreen-Verknüpfung:
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><Launch sx={{ fontSize: '1.2rem' }} /></ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography>
                                        <span style={{ fontWeight: 'bold' }}>Direkter Zugriff:</span> Ein Fingertipp genügt, um die Verwaltung zu öffnen – kein lästiges Tippen der URL mehr.
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ViewCompact sx={{ fontSize: '1.2rem' }} /></ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography>
                                        <span style={{ fontWeight: 'bold' }}>App-ähnliches Gefühl:</span> Die Seite öffnet sich im Vollbildmodus, ohne störende Browserleisten. Das sorgt für eine schönere und flüssigere Bedienung, fast wie bei einer nativen App.
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><FlashOn sx={{ fontSize: '1.2rem' }} /></ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography>
                                        <span style={{ fontWeight: 'bold' }}>Schnelleres Laden:</span> Browser können Daten effizienter cachen, wenn eine Seite als Homescreen-Verknüpfung gespeichert ist, was zu schnelleren Ladezeiten führen kann.
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                </Box>

                {/* Android Anleitung */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneAndroid /> Für Android-Nutzer (Google Chrome)
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>1.</span></ListItemIcon>
                            <ListItemText primary="Öffne diese Website mit Google Chrome." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>2.</span></ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography>
                                        Tippe oben rechts im Browser auf das <span style={{ fontWeight: 'bold' }}>Menü-Symbol</span> (<MoreVert sx={{ verticalAlign: 'middle' }} /> drei Punkte oder ein Pfeil nach oben).
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>3.</span></ListItemIcon>
                            <ListItemText primary={
                                <Typography>
                                    Wähle im Menü die Option <span style={{ fontWeight: 'bold' }}>„Zum Startbildschirm hinzufügen“</span> oder <span style={{ fontWeight: 'bold' }}>„App installieren“</span>.
                                </Typography>
                            } />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>4.</span></ListItemIcon>
                            <ListItemText primary={
                                <Typography>
                                    Bestätige den Namen der Verknüpfung und tippe auf <span style={{ fontWeight: 'bold' }}>„Hinzufügen“</span> oder <span style={{ fontWeight: 'bold' }}>„Installieren“</span>.
                                </Typography>
                            } />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>5.</span></ListItemIcon>
                            <ListItemText primary={
                                <Typography>
                                    <span style={{ fontWeight: 'bold' }}>Fertig!</span> Die App-Verknüpfung erscheint nun auf deinem Homescreen.
                                </Typography>
                            } />
                        </ListItem>
                    </List>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* iOS Anleitung */}
                <Box>
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Apple /> Für iOS-Nutzer (Safari)
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>1.</span></ListItemIcon>
                            <ListItemText primary="Öffne diese Website mit Safari." />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>2.</span></ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography>
                                        Tippe unten in der Mitte des Browsers auf das <span style={{ fontWeight: 'bold' }}>Teilen-Symbol</span> (<IosShareIcon sx={{ verticalAlign: 'middle' }} /> Quadrat mit Pfeil nach oben).
                                    </Typography>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>3.</span></ListItemIcon>
                            <ListItemText primary={
                                <Typography>
                                    Scrolle in den Optionen nach unten und wähle <span style={{ fontWeight: 'bold' }}>„Zum Home-Bildschirm“</span>.
                                </Typography>
                            } />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>4.</span></ListItemIcon>
                            <ListItemText primary={
                                <Typography>
                                    Bestätige den Namen der Verknüpfung und tippe oben rechts auf <span style={{ fontWeight: 'bold' }}>„Hinzufügen“</span>.
                                </Typography>
                            } />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>5.</span></ListItemIcon>
                            <ListItemText primary={
                                <Typography>
                                    <span style={{ fontWeight: 'bold' }}>Fertig!</span> Das App-Symbol erscheint nun auf deinem iPhone- oder iPad-Homescreen.
                                </Typography>
                            } />
                        </ListItem>
                    </List>
                </Box>

                <Typography variant="body2" sx={{ mt: 3, fontStyle: 'italic', textAlign: 'center' }}>
                    Wir hoffen, diese kleine Anleitung hilft dir dabei, deine Pokémon GO Verwaltung noch effizienter zu nutzen!
                </Typography>
            </Paper>
        </Box>
    );
};

export default Start;